import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import type { ClientBuildConfig } from '../config/buildConfig';

export type AccessTokenProvider = () => Promise<string>;

export class NaaUnsupportedError extends Error {
  constructor() {
    super('Nested App Authentication is not supported by this Outlook client.');
    this.name = 'NaaUnsupportedError';
  }
}

function isNaaSupported(): boolean {
  return Boolean(Office.context.requirements?.isSetSupported('NestedAppAuth', '1.1'));
}

export function createNaaAuthProvider(config: ClientBuildConfig): AccessTokenProvider {
  let msalInstancePromise: Promise<IPublicClientApplication> | undefined;

  const getMsalInstance = async (): Promise<IPublicClientApplication> => {
    if (!isNaaSupported()) {
      throw new NaaUnsupportedError();
    }

    msalInstancePromise ??= createNestablePublicClientApplication({
      auth: {
        clientId: config.clientId,
        authority: config.authority,
      },
      cache: {
        cacheLocation: 'sessionStorage',
      },
    });

    return msalInstancePromise;
  };

  return async function getAccessToken(): Promise<string> {
    const instance = await getMsalInstance();
    const request = { scopes: config.scopes };

    try {
      const result = await instance.acquireTokenSilent(request);
      return result.accessToken;
    } catch (error) {
      if (!(error instanceof InteractionRequiredAuthError)) {
        throw error;
      }

      const result = await instance.acquireTokenPopup(request);
      return result.accessToken;
    }
  };
}
